import { create } from 'zustand';
import { diagnosticsApi } from '../api/diagnostics';
import type {
  DiagnosticForm,
  FormPortal,
  FormResponse,
  FormDiagnostic,
  DiagnosticFramework,
  FormStatus,
  CreateDiagnosticFormRequest,
  UpdateDiagnosticFormRequest,
  UpdateFormPortalRequest,
  GenerateAnalysisRequest,
  GenerateFormRequest,
} from '@affogato/shared';

// Extended form type with response count and portal
export interface DiagnosticFormWithMeta extends DiagnosticForm {
  responseCount?: number;
  portal?: FormPortal;
  client?: {
    id: string;
    name: string;
    company: string | null;
  };
}

interface DiagnosticsState {
  // Forms
  forms: DiagnosticFormWithMeta[];
  selectedForm: DiagnosticFormWithMeta | null;
  isLoading: boolean;
  error: string | null;

  // Templates
  templates: Array<{
    id: string;
    name: string;
    description: string;
    framework: DiagnosticFramework;
    questionCount: number;
    sectionCount: number;
  }>;

  // Responses for selected form
  responses: FormResponse[];
  selectedResponse: FormResponse | null;

  // Diagnostics/Analysis for selected form
  diagnostics: FormDiagnostic[];
  selectedDiagnostic: FormDiagnostic | null;

  // Actions - Forms
  fetchForms: (params?: { clientId?: string; status?: FormStatus; framework?: DiagnosticFramework }) => Promise<void>;
  fetchForm: (id: string) => Promise<void>;
  fetchTemplates: () => Promise<void>;
  createForm: (data: CreateDiagnosticFormRequest) => Promise<DiagnosticForm>;
  createFromTemplate: (templateId: string) => Promise<DiagnosticForm>;
  generateFormWithAI: (data: GenerateFormRequest) => Promise<DiagnosticForm>;
  updateForm: (id: string, data: UpdateDiagnosticFormRequest) => Promise<void>;
  deleteForm: (id: string) => Promise<void>;
  duplicateForm: (id: string) => Promise<DiagnosticForm>;
  publishForm: (id: string) => Promise<void>;
  archiveForm: (id: string) => Promise<void>;
  associateToClient: (formId: string, clientId: string) => Promise<{ projectId: string; projectName: string }>;

  // Actions - Portal
  createOrUpdatePortal: (formId: string, data: UpdateFormPortalRequest) => Promise<FormPortal>;
  regenerateToken: (formId: string) => Promise<string>;

  // Actions - Responses
  fetchResponses: (formId: string) => Promise<void>;
  fetchResponse: (formId: string, responseId: string) => Promise<void>;
  deleteResponse: (formId: string, responseId: string) => Promise<void>;
  setSelectedResponse: (response: FormResponse | null) => void;

  // Actions - Diagnostics
  generateAnalysis: (formId: string, data: GenerateAnalysisRequest) => Promise<FormDiagnostic>;
  fetchDiagnostics: (formId: string) => Promise<void>;
  fetchDiagnostic: (formId: string, diagnosticId: string) => Promise<void>;
  updateDiagnostic: (formId: string, diagnosticId: string, data: { title?: string; content?: string; status?: 'draft' | 'published' }) => Promise<void>;
  deleteDiagnostic: (formId: string, diagnosticId: string) => Promise<void>;
  setSelectedDiagnostic: (diagnostic: FormDiagnostic | null) => void;

  // Utils
  clearError: () => void;
  setSelectedForm: (form: DiagnosticFormWithMeta | null) => void;
}

export const useDiagnosticsStore = create<DiagnosticsState>((set) => ({
  forms: [],
  selectedForm: null,
  isLoading: false,
  error: null,
  templates: [],
  responses: [],
  selectedResponse: null,
  diagnostics: [],
  selectedDiagnostic: null,

  // ========== FORMS ==========

  fetchForms: async (params) => {
    set({ isLoading: true, error: null });
    try {
      const response = await diagnosticsApi.getAll(params);
      set({ forms: response.forms as DiagnosticFormWithMeta[], isLoading: false });
    } catch {
      set({ error: 'Error al cargar formularios', isLoading: false });
    }
  },

  fetchForm: async (id) => {
    set({ isLoading: true, error: null });
    try {
      const response = await diagnosticsApi.getById(id);
      set({ selectedForm: response.form as DiagnosticFormWithMeta, isLoading: false });
    } catch {
      set({ error: 'Error al cargar formulario', isLoading: false });
    }
  },

  fetchTemplates: async () => {
    try {
      const response = await diagnosticsApi.getTemplates();
      set({ templates: response.templates });
    } catch {
      // Silent fail for templates
    }
  },

  createForm: async (data) => {
    set({ isLoading: true, error: null });
    try {
      const response = await diagnosticsApi.create(data);
      set((state) => ({
        forms: [response.form as DiagnosticFormWithMeta, ...state.forms],
        isLoading: false,
      }));
      return response.form;
    } catch {
      set({ error: 'Error al crear formulario', isLoading: false });
      throw new Error('Error al crear formulario');
    }
  },

  createFromTemplate: async (templateId) => {
    set({ isLoading: true, error: null });
    try {
      const response = await diagnosticsApi.createFromTemplate(templateId);
      set((state) => ({
        forms: [response.form as DiagnosticFormWithMeta, ...state.forms],
        isLoading: false,
      }));
      return response.form;
    } catch {
      set({ error: 'Error al crear desde plantilla', isLoading: false });
      throw new Error('Error al crear desde plantilla');
    }
  },

  generateFormWithAI: async (data) => {
    set({ isLoading: true, error: null });
    try {
      // First, generate the form structure with AI
      const generated = await diagnosticsApi.generateForm(data);

      // Then create the form with the generated structure
      const response = await diagnosticsApi.create({
        title: generated.title,
        description: generated.description,
        framework: data.framework || 'custom',
        structure: generated.structure as CreateDiagnosticFormRequest['structure'],
      });

      set((state) => ({
        forms: [response.form as DiagnosticFormWithMeta, ...state.forms],
        isLoading: false,
      }));
      return response.form;
    } catch (error) {
      const message = error instanceof Error && error.message.includes('AI_NOT_CONFIGURED')
        ? 'La IA no está configurada'
        : 'Error al generar formulario con IA';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  updateForm: async (id, data) => {
    set({ isLoading: true, error: null });
    try {
      const response = await diagnosticsApi.update(id, data);
      set((state) => ({
        forms: state.forms.map((f) => (f.id === id ? { ...f, ...response.form } : f)),
        selectedForm: state.selectedForm?.id === id
          ? { ...state.selectedForm, ...response.form }
          : state.selectedForm,
        isLoading: false,
      }));
    } catch {
      set({ error: 'Error al actualizar formulario', isLoading: false });
      throw new Error('Error al actualizar formulario');
    }
  },

  deleteForm: async (id) => {
    set({ isLoading: true, error: null });
    try {
      await diagnosticsApi.delete(id);
      set((state) => ({
        forms: state.forms.filter((f) => f.id !== id),
        selectedForm: state.selectedForm?.id === id ? null : state.selectedForm,
        isLoading: false,
      }));
    } catch {
      set({ error: 'Error al eliminar formulario', isLoading: false });
      throw new Error('Error al eliminar formulario');
    }
  },

  duplicateForm: async (id) => {
    set({ isLoading: true, error: null });
    try {
      const response = await diagnosticsApi.duplicate(id);
      set((state) => ({
        forms: [response.form as DiagnosticFormWithMeta, ...state.forms],
        isLoading: false,
      }));
      return response.form;
    } catch {
      set({ error: 'Error al duplicar formulario', isLoading: false });
      throw new Error('Error al duplicar formulario');
    }
  },

  publishForm: async (id) => {
    set({ isLoading: true, error: null });
    try {
      await diagnosticsApi.publish(id);
      set((state) => ({
        forms: state.forms.map((f) => (f.id === id ? { ...f, status: 'published' as FormStatus } : f)),
        selectedForm: state.selectedForm?.id === id
          ? { ...state.selectedForm, status: 'published' as FormStatus }
          : state.selectedForm,
        isLoading: false,
      }));
    } catch {
      set({ error: 'Error al publicar formulario', isLoading: false });
      throw new Error('Error al publicar formulario');
    }
  },

  archiveForm: async (id) => {
    set({ isLoading: true, error: null });
    try {
      await diagnosticsApi.archive(id);
      set((state) => ({
        forms: state.forms.map((f) => (f.id === id ? { ...f, status: 'archived' as FormStatus } : f)),
        selectedForm: state.selectedForm?.id === id
          ? { ...state.selectedForm, status: 'archived' as FormStatus }
          : state.selectedForm,
        isLoading: false,
      }));
    } catch {
      set({ error: 'Error al archivar formulario', isLoading: false });
      throw new Error('Error al archivar formulario');
    }
  },

  associateToClient: async (formId, clientId) => {
    set({ isLoading: true, error: null });
    try {
      const response = await diagnosticsApi.associate(formId, { clientId });
      set((state) => ({
        forms: state.forms.map((f) =>
          f.id === formId ? { ...f, clientId, projectId: response.projectId } : f
        ),
        selectedForm: state.selectedForm?.id === formId
          ? { ...state.selectedForm, clientId, projectId: response.projectId }
          : state.selectedForm,
        isLoading: false,
      }));
      return { projectId: response.projectId, projectName: response.projectName };
    } catch {
      set({ error: 'Error al asociar a cliente', isLoading: false });
      throw new Error('Error al asociar a cliente');
    }
  },

  // ========== PORTAL ==========

  createOrUpdatePortal: async (formId, data) => {
    set({ isLoading: true, error: null });
    try {
      const response = await diagnosticsApi.createOrUpdatePortal(formId, data);
      set((state) => ({
        forms: state.forms.map((f) => (f.id === formId ? { ...f, portal: response.portal } : f)),
        selectedForm: state.selectedForm?.id === formId
          ? { ...state.selectedForm, portal: response.portal }
          : state.selectedForm,
        isLoading: false,
      }));
      return response.portal;
    } catch {
      set({ error: 'Error al configurar portal', isLoading: false });
      throw new Error('Error al configurar portal');
    }
  },

  regenerateToken: async (formId) => {
    set({ isLoading: true, error: null });
    try {
      const response = await diagnosticsApi.regeneratePortalToken(formId);
      set((state) => {
        const updatePortal = (f: DiagnosticFormWithMeta) =>
          f.id === formId && f.portal ? { ...f, portal: { ...f.portal, token: response.token } } : f;
        return {
          forms: state.forms.map(updatePortal),
          selectedForm: state.selectedForm?.id === formId && state.selectedForm.portal
            ? { ...state.selectedForm, portal: { ...state.selectedForm.portal, token: response.token } }
            : state.selectedForm,
          isLoading: false,
        };
      });
      return response.token;
    } catch {
      set({ error: 'Error al regenerar token', isLoading: false });
      throw new Error('Error al regenerar token');
    }
  },

  // ========== RESPONSES ==========

  fetchResponses: async (formId) => {
    set({ isLoading: true, error: null });
    try {
      const response = await diagnosticsApi.getResponses(formId);
      set({ responses: response.responses, isLoading: false });
    } catch {
      set({ error: 'Error al cargar respuestas', isLoading: false });
    }
  },

  fetchResponse: async (formId, responseId) => {
    set({ isLoading: true, error: null });
    try {
      const response = await diagnosticsApi.getResponse(formId, responseId);
      set({ selectedResponse: response.response, isLoading: false });
    } catch {
      set({ error: 'Error al cargar respuesta', isLoading: false });
    }
  },

  deleteResponse: async (formId, responseId) => {
    set({ isLoading: true, error: null });
    try {
      await diagnosticsApi.deleteResponse(formId, responseId);
      set((state) => ({
        responses: state.responses.filter((r) => r.id !== responseId),
        selectedResponse: state.selectedResponse?.id === responseId ? null : state.selectedResponse,
        isLoading: false,
      }));
    } catch {
      set({ error: 'Error al eliminar respuesta', isLoading: false });
      throw new Error('Error al eliminar respuesta');
    }
  },

  setSelectedResponse: (response) => set({ selectedResponse: response }),

  // ========== DIAGNOSTICS ==========

  generateAnalysis: async (formId, data) => {
    set({ isLoading: true, error: null });
    try {
      const response = await diagnosticsApi.generateAnalysis(formId, data);
      set((state) => ({
        diagnostics: [response.diagnostic, ...state.diagnostics],
        isLoading: false,
      }));
      return response.diagnostic;
    } catch (error) {
      const message = error instanceof Error && error.message.includes('AI_NOT_CONFIGURED')
        ? 'La IA no está configurada'
        : 'Error al generar análisis';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  fetchDiagnostics: async (formId) => {
    set({ isLoading: true, error: null });
    try {
      const response = await diagnosticsApi.getAnalysisList(formId);
      set({ diagnostics: response.diagnostics, isLoading: false });
    } catch {
      set({ error: 'Error al cargar diagnósticos', isLoading: false });
    }
  },

  fetchDiagnostic: async (formId, diagnosticId) => {
    set({ isLoading: true, error: null });
    try {
      const response = await diagnosticsApi.getAnalysis(formId, diagnosticId);
      set({ selectedDiagnostic: response.diagnostic, isLoading: false });
    } catch {
      set({ error: 'Error al cargar diagnóstico', isLoading: false });
    }
  },

  updateDiagnostic: async (formId, diagnosticId, data) => {
    set({ isLoading: true, error: null });
    try {
      const response = await diagnosticsApi.updateAnalysis(formId, diagnosticId, data);
      set((state) => ({
        diagnostics: state.diagnostics.map((d) => (d.id === diagnosticId ? response.diagnostic : d)),
        selectedDiagnostic: state.selectedDiagnostic?.id === diagnosticId
          ? response.diagnostic
          : state.selectedDiagnostic,
        isLoading: false,
      }));
    } catch {
      set({ error: 'Error al actualizar diagnóstico', isLoading: false });
      throw new Error('Error al actualizar diagnóstico');
    }
  },

  deleteDiagnostic: async (formId, diagnosticId) => {
    set({ isLoading: true, error: null });
    try {
      await diagnosticsApi.deleteAnalysis(formId, diagnosticId);
      set((state) => ({
        diagnostics: state.diagnostics.filter((d) => d.id !== diagnosticId),
        selectedDiagnostic: state.selectedDiagnostic?.id === diagnosticId ? null : state.selectedDiagnostic,
        isLoading: false,
      }));
    } catch {
      set({ error: 'Error al eliminar diagnóstico', isLoading: false });
      throw new Error('Error al eliminar diagnóstico');
    }
  },

  setSelectedDiagnostic: (diagnostic) => set({ selectedDiagnostic: diagnostic }),

  // ========== UTILS ==========

  clearError: () => set({ error: null }),
  setSelectedForm: (form) => set({ selectedForm: form }),
}));
